export type Band = 'strong' | 'good' | 'stretch';
export type MapOrgKind = 'company' | 'agency' | 'provider';
export type MapSort = 'best' | 'soonest' | 'newest';

export interface MapCapabilities {
  copy_link: boolean;
  nudge: boolean;
  bulk_invite: boolean;
  invite_person: boolean;
  reminder: boolean;
  ask: boolean;
  place: boolean;
  shortlist: boolean;
  send_shortlist: boolean;
}

export interface PersonRef {
  id: string;
  name: string;
  initials: string;
  href: string;
  photo_url?: string | null;
}

export interface Evidence {
  text: string;
  source: string;
  at_secs?: number | null;
  quoted: boolean;
}

export interface MapChange {
  id: string;
  kind: 'role_changed' | 'new_interviews' | 'gap';
  label: string;
  at_label: string;
  sentence: string;
  action: { label: string; href: string };
}

export interface HomeRole {
  id: string;
  title: string;
  partner: string;
  place_line: string;
  status: { kind: 'ready' | 'new_fits' | 'sent' | 'no_fit'; label: string };
  top: Array<PersonRef & { reason: string; band: Band; score?: number }>;
  summary: string | null;
  footer: string;
  href: string;
}

export type PoolFilter = 'ready' | 'no_fit' | 'cv_only';

export interface PoolRow {
  person: PersonRef;
  meta: string;
  group: string;
  best_fit: { title: string; band: Band; href: string; score?: number } | null;
  spoken: boolean;
  filters: PoolFilter[];
}

export interface MapCohort {
  id: string;
  name: string;
  status_label: string;
  talked: number;
  total: number;
  progress_line: string;
  invite_path: string | null;
  not_started: number;
  imported: boolean;
}

export interface MapHomeV2 {
  sample: boolean;
  org_kind: MapOrgKind;
  org_name: string;
  remapped_label: string | null;
  greeting: string;
  briefing: string;
  suggestions: Array<{
    label: string;
    kind: 'query' | 'intake';
    query?: string;
  }>;
  changes: MapChange[];
  roles_lead: string;
  roles: HomeRole[];
  pool: PoolRow[];
  pool_counts: {
    everyone: number;
    ready: number;
    no_fit: number;
    cv_only: number;
  };
  cv_only: { count: number; sentence: string; detail: string };
  cohorts_lead: string;
  cohorts: MapCohort[];
  capabilities: MapCapabilities;
}

export interface FilterTokenData {
  id: string;
  label: string;
  kind: 'role' | 'location' | 'signal';
}

export interface SearchResult {
  person: PersonRef;
  headline: string;
  availability: { label: string; ready: boolean };
  evidence: {
    text: string;
    highlights: Array<[number, number]>;
    quoted: boolean;
    from_cv: boolean;
  };
  tags: Array<{ label: string; warn: boolean }>;
  source_label: string;
  band: Band;
  score?: number;
  cv_only: boolean;
}

export interface MapSearchV2 {
  sample: boolean;
  org_kind: MapOrgKind;
  query: string;
  answer: string;
  explain: string;
  tokens: FilterTokenData[];
  dropped: FilterTokenData[];
  sort: MapSort;
  total: number;
  results: SearchResult[];
  relaxed: { count: number; token_id: string; token_label: string } | null;
  roles: Array<{ id: string; title: string }>;
  capabilities: MapCapabilities;
}

export interface Recommendation {
  person: PersonRef;
  first_name: string;
  headline: string;
  band: Band;
  score?: number;
  evidence: Evidence[];
  gap: string | null;
  confirmed: string | null;
  cv_only: boolean;
  summary: string | null;
  ask_label: string | null;
  on_shortlist: boolean;
  folded: boolean;
}

export interface MapRoleV2 {
  sample: boolean;
  org_kind: MapOrgKind;
  role: {
    id: string;
    title: string;
    partner: string;
    place_line: string;
    summary: string;
    remapped_label: string | null;
  };
  needs: string[];
  recommendations: Recommendation[];
  folded_line: string | null;
  shortlist: {
    people: PersonRef[];
    contact_name: string | null;
    lead: string;
    sent_line: string | null;
  };
  learned: string | null;
  capabilities: MapCapabilities;
}

export interface Turn {
  speaker: 'melon' | 'person';
  name: string;
  text: string;
  at_secs: number;
}

export interface MapDocument {
  id: string;
  title: string;
  detail: string;
  status: 'received' | 'waiting';
  href: string | null;
}

export interface MapPersonV2 {
  sample: boolean;
  org_kind: MapOrgKind;
  person: PersonRef & {
    first_name: string;
    summary: string;
    chips: string[];
    score?: number;
    placed: string | null;
  };
  interview: {
    meta: string;
    audio_url: string | null;
    duration_secs: number;
    chapters: Array<{ label: string; at_secs: number }>;
    turns: Turn[];
  } | null;
  themes: Array<{ theme: string; evidence: Evidence }>;
  feedback: { headline: string; body: string } | null;
  documents: MapDocument[];
  fits: Array<{
    role_id: string;
    title: string;
    note: string;
    band: Band;
    score?: number;
    href: string;
  }>;
  checks: Array<{ name: string; state: 'confirmed' | 'missing' | 'uploaded' }>;
  activity: Array<{ text: string; at_label: string; today: boolean }>;
  capabilities: MapCapabilities;
}

export const BAND_STYLE: Record<
  Band,
  { label: string; bg: string; fg: string }
> = {
  strong: { label: 'Strong', bg: '#E3F1EA', fg: '#17623F' },
  good: { label: 'Good', bg: '#E6EBF6', fg: '#032170' },
  stretch: { label: 'Stretch', bg: '#FBEFD9', fg: '#7A4B00' },
};

export function bandStyle(band: string) {
  return BAND_STYLE[(band as Band) in BAND_STYLE ? (band as Band) : 'good'];
}

export function highlightSegments(
  text: string,
  ranges: Array<[number, number]>
): Array<{ text: string; mark: boolean }> {
  const sorted = [...ranges]
    .filter(([start, end]) => end > start)
    .sort((a, b) => a[0] - b[0]);
  const out: Array<{ text: string; mark: boolean }> = [];
  let cursor = 0;
  for (const [rawStart, rawEnd] of sorted) {
    const start = Math.max(rawStart, cursor);
    const end = Math.min(rawEnd, text.length);
    if (start >= end) continue;
    if (start > cursor)
      out.push({ text: text.slice(cursor, start), mark: false });
    out.push({ text: text.slice(start, end), mark: true });
    cursor = end;
  }
  if (cursor < text.length) out.push({ text: text.slice(cursor), mark: false });
  return out;
}

export function searchQuery(input: {
  q: string;
  dropped: string[];
  sort: MapSort;
  drop?: string;
  restore?: string;
}) {
  let dropped = input.dropped.filter((id) => id !== input.restore);
  if (input.drop && !dropped.includes(input.drop))
    dropped = [...dropped, input.drop];
  const query: Record<string, string> = { q: input.q };
  if (dropped.length) query.drop = dropped.join(',');
  if (input.sort !== 'best') query.sort = input.sort;
  return query;
}

export function playedBars(current: number, total: number, bars: number) {
  if (!total || total <= 0) return 0;
  const ratio = Math.min(1, Math.max(0, current / total));
  return Math.round(ratio * bars);
}

export function formatClock(secs: number) {
  const whole = Math.max(0, Math.floor(secs || 0));
  const minutes = Math.floor(whole / 60);
  const seconds = whole % 60;
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
}

export function changeCopy(change: MapChange) {
  return {
    label: change.label,
    when: change.at_label,
    sentence: change.sentence,
    action: change.action,
  };
}

interface ApplicantInterviewInput {
  audio_url: string | null;
  duration_seconds: number | null;
  question_count: number | null;
  transcript: Array<{
    role: string;
    message: string;
    time_in_call_secs: number;
  }>;
  analysis: {
    standAgainstSpecs?: Array<{
      specItemId: string;
      evidence_time_secs?: number | null;
    }>;
  } | null;
}

function lengthLabel(secs: number) {
  const minutes = Math.round(secs / 60);
  if (minutes < 1) return 'Under a minute';
  return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
}

/** Shapes a screening interview for the map interview player. */
export function applicantInterview(
  review: ApplicantInterviewInput | null | undefined,
  fullName: string,
  specs: Array<{ id: string; value: string }>
): NonNullable<MapPersonV2['interview']> | null {
  if (!review) return null;
  const transcript = review.transcript || [];
  if (!transcript.length && !review.audio_url) return null;

  const firstName = fullName.trim().split(/\s+/)[0] || fullName;
  const turns: Turn[] = transcript.map((turn) => ({
    speaker: turn.role === 'agent' ? 'melon' : 'person',
    name: turn.role === 'agent' ? 'Melon' : firstName,
    text: turn.message,
    at_secs: turn.time_in_call_secs || 0,
  }));
  const duration =
    review.duration_seconds || turns[turns.length - 1]?.at_secs || 0;

  const chapters = (review.analysis?.standAgainstSpecs || [])
    .flatMap((check) => {
      const spec = specs.find((item) => item.id === check.specItemId);
      const at = check.evidence_time_secs;
      return spec && typeof at === 'number'
        ? [{ label: spec.value, at_secs: at }]
        : [];
    })
    .sort((a, b) => a.at_secs - b.at_secs);

  const questions = review.question_count;
  const meta = [
    lengthLabel(duration),
    questions
      ? `${questions} ${questions === 1 ? 'question' : 'questions'}`
      : '',
  ]
    .filter(Boolean)
    .join(' · ');

  return {
    meta,
    audio_url: review.audio_url,
    duration_secs: duration,
    chapters,
    turns,
  };
}

const THEME_LIMIT = 4;

const unquote = (text: string) =>
  text
    .trim()
    .replace(/^["\u201C]+|["\u201D]+$/g, '')
    .trim();

/** Picks up to four cards for "What Melon heard", interview first. */
export function applicantThemes(input: {
  analysis: {
    standAgainstSpecs?: Array<{
      specItemId: string;
      transcriptEvidence?: string | null;
      evidence_time_secs?: number | null;
    }>;
  } | null;
  dimensions: Array<{ specItemId: string; evidence: string[]; note: string }>;
  specs: Array<{ id: string; value: string }>;
}): MapPersonV2['themes'] {
  const specValue = (id: string) =>
    input.specs.find((item) => item.id === id)?.value;
  const seen = new Set<string>();
  const once = (id: string) => {
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  };

  if (input.analysis) {
    return (input.analysis.standAgainstSpecs || [])
      .flatMap((check) => {
        const theme = specValue(check.specItemId);
        const text = unquote(check.transcriptEvidence || '');
        if (!theme || !text || !once(check.specItemId)) return [];
        return [
          {
            theme,
            evidence: {
              text,
              source: 'From the interview',
              at_secs: check.evidence_time_secs ?? null,
              quoted: true,
            },
          },
        ];
      })
      .slice(0, THEME_LIMIT);
  }

  return input.dimensions
    .flatMap((dimension) => {
      const theme = specValue(dimension.specItemId);
      const quote = dimension.evidence.map(unquote).find(Boolean);
      const text = quote || dimension.note.trim();
      if (!theme || !text || !once(dimension.specItemId)) return [];
      return [
        {
          theme,
          evidence: {
            text,
            source: 'From the application',
            at_secs: null,
            quoted: Boolean(quote),
          },
        },
      ];
    })
    .slice(0, THEME_LIMIT);
}

export function applicantDocuments(input: {
  resumeUrl: string | null | undefined;
  appliedLabel: string;
}): MapDocument[] {
  if (input.resumeUrl) {
    return [
      {
        id: 'cv',
        title: 'CV',
        detail: `Sent with the application ${input.appliedLabel}`,
        status: 'received',
        href: input.resumeUrl,
      },
    ];
  }
  return [
    {
      id: 'cv',
      title: 'CV',
      detail: 'Not uploaded yet',
      status: 'waiting',
      href: null,
    },
  ];
}

/** Builds "What has happened" from timestamps the applicant already has. */
export function applicantActivity(input: {
  roleTitle: string;
  appliedLabel: string;
  invited: boolean;
  completedLabel: string | null;
  statusLabel: string;
}): MapPersonV2['activity'] {
  return [
    { text: input.statusLabel, at_label: 'Current status', today: true },
    ...(input.completedLabel
      ? [
          {
            text: 'Finished the interview with Melon',
            at_label: input.completedLabel,
            today: false,
          },
        ]
      : []),
    ...(input.invited
      ? [
          {
            text: 'Invited to talk to Melon',
            at_label: input.completedLabel
              ? 'Before the interview'
              : 'Waiting on the interview',
            today: false,
          },
        ]
      : []),
    {
      text: `Applied for ${input.roleTitle}`,
      at_label: input.appliedLabel,
      today: false,
    },
  ];
}

export function activeTurn(turns: Turn[], current: number) {
  let index = -1;
  turns.forEach((turn, at) => {
    if (turn.at_secs <= current) index = at;
  });
  return index;
}
